import React, { useState } from 'react';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy,
    useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical, Trash2, Plus, GripHorizontal } from 'lucide-react';

// Sortable Item Component
const SortableQuestionItem = ({ question, index, onUpdate, onDelete }) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: question.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        zIndex: isDragging ? 50 : 'auto',
        position: 'relative',
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`bg-white border rounded-lg p-4 mb-3 transition-shadow ${isDragging ? 'shadow-lg border-indigo-400 ring-2 ring-indigo-50' : 'border-gray-200 hover:border-gray-300'
                }`}
        >
            <div className="flex items-start gap-3">
                {/* Drag Handle */}
                <div
                    {...attributes}
                    {...listeners}
                    className="mt-2 text-gray-400 cursor-grab hover:text-gray-600 touch-none"
                >
                    <GripVertical size={20} />
                </div>

                {/* Content */}
                <div className="flex-1 space-y-3">
                    {/* Question Text & Delete */}
                    <div className="flex justify-between items-start gap-4">
                        <input
                            type="text"
                            value={question.question_text}
                            onChange={(e) => onUpdate(question.id, 'question_text', e.target.value)}
                            placeholder="Type your question here..."
                            className="flex-1 text-lg font-medium text-gray-800 placeholder-gray-400 border-none focus:ring-0 focus:outline-none bg-transparent p-0"
                        />
                        <button
                            onClick={() => onDelete(question.id)}
                            className="text-gray-400 hover:text-red-500 transition-colors"
                            title="Delete Question"
                        >
                            <Trash2 size={18} />
                        </button>
                    </div>

                    {/* Type & Required Toggle */}
                    <div className="flex items-center gap-4 text-sm">
                        <select
                            value={question.question_type}
                            onChange={(e) => onUpdate(question.id, 'question_type', e.target.value)}
                            className="block w-40 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-1.5 border"
                        >
                            <option value="text">Text Answer</option>
                            <option value="rating">Rating (1-5)</option>
                            <option value="multiple_choice">Multiple Choice</option>
                            <option value="boolean">Yes/No</option>
                        </select>

                        <label className="flex items-center gap-2 cursor-pointer text-gray-600 select-none">
                            <input
                                type="checkbox"
                                checked={question.is_required}
                                onChange={(e) => onUpdate(question.id, 'is_required', e.target.checked)}
                                className="rounded border-gray-300 text-indigo-600 shadow-sm focus:ring-indigo-500"
                            />
                            Required
                        </label>
                    </div>

                    {/* Options for Multiple Choice */}
                    {question.question_type === 'multiple_choice' && (
                        <div className="mt-2 pl-2 border-l-2 border-indigo-100 space-y-2">
                            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Options</span>
                            {(question.options || []).map((option, idx) => (
                                <div key={idx} className="flex items-center gap-2">
                                    <div className="w-4 h-4 rounded-full border border-gray-300" />
                                    <input
                                        type="text"
                                        value={option}
                                        onChange={(e) => {
                                            const newOptions = [...(question.options || [])];
                                            newOptions[idx] = e.target.value;
                                            onUpdate(question.id, 'options', newOptions);
                                        }}
                                        placeholder={`Option ${idx + 1}`}
                                        className="flex-1 text-sm border-gray-200 rounded-md py-1 px-2 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 focus:outline-none"
                                    />
                                    <button
                                        onClick={() => {
                                            const newOptions = (question.options || []).filter((_, i) => i !== idx);
                                            onUpdate(question.id, 'options', newOptions);
                                        }}
                                        className="text-gray-400 hover:text-red-500"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>
                            ))}
                            <button
                                onClick={() => {
                                    const newOptions = [...(question.options || []), ''];
                                    onUpdate(question.id, 'options', newOptions);
                                }}
                                className="text-xs text-indigo-600 hover:text-indigo-800 font-medium flex items-center gap-1"
                            >
                                <Plus size={14} /> Add Option
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

const QuestionList = ({ questions, setQuestions }) => {
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleDragEnd = (event) => {
        const { active, over } = event;

        if (active.id !== over.id) {
            const oldIndex = questions.findIndex((q) => q.id === active.id);
            const newIndex = questions.findIndex((q) => q.id === over.id);
            setQuestions(arrayMove(questions, oldIndex, newIndex));
        }
    };

    const updateQuestion = (id, field, value) => {
        setQuestions(
            questions.map((q) =>
                q.id === id ? { ...q, [field]: value } : q
            )
        );
    };

    const deleteQuestion = (id) => {
        setQuestions(questions.filter((q) => q.id !== id));
    };

    const addQuestion = () => {
        const newQuestion = {
            id: `temp-${Date.now()}`, // temp ID for frontend sortable
            question_text: '',
            question_type: 'text',
            is_required: true,
            options: [''],
        };
        setQuestions([...questions, newQuestion]);
    };

    return (
        <div className="flex flex-col h-full bg-gray-50/50 p-6 overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-800">Questions</h3>
                {/* Add Button */}
                <button
                    onClick={addQuestion}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 shadow-sm transition-all"
                >
                    <Plus size={16} />
                    Add Question
                </button>
            </div>

            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
            >
                <SortableContext
                    items={questions.map(q => q.id)}
                    strategy={verticalListSortingStrategy}
                >
                    <div className="space-y-4 pb-10">
                        {questions.length === 0 && (
                            <div className="text-center py-10 border-2 border-dashed border-gray-300 rounded-lg text-gray-500">
                                <p>No questions yet.</p>
                                <p className="text-sm mt-1">Add one manually or ask the AI to generate some!</p>
                            </div>
                        )}
                        {questions.map((question, index) => (
                            <SortableQuestionItem
                                key={question.id}
                                question={question}
                                index={index}
                                onUpdate={updateQuestion}
                                onDelete={deleteQuestion}
                            />
                        ))}
                    </div>
                </SortableContext>
            </DndContext>


        </div>
    );
};

export default QuestionList;
